import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useRouter } from '@/lib/compat/navigation';
import { getChannelSwitchClient, getClient, getLogoutClient, getPublicAuthClient, refreshAdminAccessToken, setChannelToken, setAuthToken } from '@/lib/graphql/client';
import { findChannelById } from './channel-selection';
import { resolveLoginErrorCode } from './login-error';
import { ADMIN_LOGIN, LOGOUT_MUTATION, ME_QUERY, SWITCH_CHANNEL } from '@/lib/graphql/operations/auth';

interface Channel {
  id: string;
  code: string;
  token: string;
  permissions: string[];
  defaultCurrencyCode?: string;
}

interface User {
  id: string;
  identifier: string;
  channels: Channel[];
}

interface AuthRefreshResult {
  success: boolean;
  error?: string;
  needsChannelSelection?: boolean;
}

interface LoginResult extends AuthRefreshResult {
  twoFactorRequired?: boolean;
  twoFactorToken?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  activeChannel: Channel | null;
  channelSelectionResolved: boolean;
  needsChannelSelection: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  refreshAuth: () => Promise<AuthRefreshResult>;
  logout: () => Promise<void>;
  selectChannel: (channelId: string) => void;
  switchChannel: (channelId: string) => void;
  clearChannelSelection: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Local storage key for persisting channel selection
const ACTIVE_CHANNEL_KEY = 'kayn_active_channel_id';
const ACTIVE_CHANNEL_CODE_KEY = 'kayn_active_channel_code';
const ACTIVE_CHANNEL_DATA_KEY = 'kayn_active_channel';
const PUBLIC_AUTH_PATHS = new Set(['/login', '/signup']);

function isPublicAuthPath(): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  return PUBLIC_AUTH_PATHS.has(pathname);
}

function getStoredActiveChannel(): Channel | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(ACTIVE_CHANNEL_DATA_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<Channel>;
    if (
      typeof parsed.id === 'string' &&
      typeof parsed.code === 'string' &&
      typeof parsed.token === 'string' &&
      Array.isArray(parsed.permissions)
    ) {
      return {
        id: parsed.id,
        code: parsed.code,
        token: parsed.token,
        permissions: parsed.permissions.filter((permission): permission is string => typeof permission === 'string'),
      };
    }
  } catch {
    localStorage.removeItem(ACTIVE_CHANNEL_DATA_KEY);
  }

  return null;
}

function persistChannelSelection(channel: Channel | null) {
  if (channel) {
    localStorage.setItem(ACTIVE_CHANNEL_KEY, channel.id);
    localStorage.setItem(ACTIVE_CHANNEL_CODE_KEY, channel.code);
    localStorage.setItem(ACTIVE_CHANNEL_DATA_KEY, JSON.stringify(channel));
  } else {
    localStorage.removeItem(ACTIVE_CHANNEL_KEY);
    localStorage.removeItem(ACTIVE_CHANNEL_CODE_KEY);
    localStorage.removeItem(ACTIVE_CHANNEL_DATA_KEY);
  }
  // Notify same-tab listeners (TenantProvider, etc.) — `storage` event
  // doesn't fire for the originating tab, so we dispatch a custom event.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kayn:channel-switched'));
  }
}

function clearPersistedChannelSelection() {
  localStorage.removeItem(ACTIVE_CHANNEL_KEY);
  localStorage.removeItem(ACTIVE_CHANNEL_CODE_KEY);
  localStorage.removeItem(ACTIVE_CHANNEL_DATA_KEY);
}

function getStoredAuthChannelId(): string | null {
  const token = sessionStorage.getItem('kayn_auth_token');
  if (!token) return null;

  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const normalizedPayload = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(normalizedPayload)) as { channelId?: string | number };
    return decoded.channelId == null ? null : String(decoded.channelId);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [activeChannel, setActiveChannelState] = useState<Channel | null>(() => getStoredActiveChannel());
  const [loading, setLoading] = useState(true);
  const [channelSelectionResolved, setChannelSelectionResolved] = useState(false);
  const router = useRouter();
  const isLoggingOut = useRef(false);
  // Ref so hydrateFromMe can read the current channel without being in its deps
  const activeChannelRef = useRef<Channel | null>(activeChannel);

  // Keep ref in sync so hydrateFromMe always reads the latest channel
  useEffect(() => { activeChannelRef.current = activeChannel; }, [activeChannel]);

  // User needs to select a channel if:
  // - They're logged in
  // - They have multiple channels
  // - No channel is currently selected
  const needsChannelSelection = !!user && user.channels.length > 1 && !activeChannel;

  const refreshAccessTokenFromCookie = useCallback(async (): Promise<boolean> => {
    return refreshAdminAccessToken();
  }, []);

  // Wrapper that sets both state AND token synchronously to avoid race conditions.
  // It also refreshes the admin JWT when the selected store differs from the
  // channel embedded in the current token; otherwise backend admin resolvers keep
  // using the login-default channel even though the tenant token changed.
  const setActiveChannel = useCallback((channel: Channel | null) => {
    if (channel) {
      if (getStoredAuthChannelId() !== String(channel.id)) {
        // Capture current state before optimistic updates so we can revert on failure
        const previousChannel = getStoredActiveChannel();
        setChannelToken(channel.token);
        persistChannelSelection(channel);

        void (async () => {
          try {
            const result = await getChannelSwitchClient().mutation(SWITCH_CHANNEL, {
              channelId: Number(channel.id),
            }).toPromise();
            const nextToken = (result.data as any)?.switchChannel?.token;
            if (nextToken) {
              setAuthToken(nextToken);
              setActiveChannelState(channel);
            } else {
              throw new Error('No token in switchChannel response');
            }
          } catch (error) {
            console.warn('Failed to refresh channel-scoped admin token:', error);
            // Revert optimistic updates so UI and token stay consistent with the backend
            if (previousChannel) {
              setChannelToken(previousChannel.token);
              persistChannelSelection(previousChannel);
            } else {
              setChannelToken(null);
              clearPersistedChannelSelection();
            }
          }
        })();
        return;
      }

      setChannelToken(channel.token);
      persistChannelSelection(channel);
    } else {
      setChannelToken(null);
      clearPersistedChannelSelection();
    }

    setActiveChannelState(channel);
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Listen for auth-expired events from query/mutation hooks
  useEffect(() => {
    const handleAuthExpired = () => {
      if (isLoggingOut.current) return;
      isLoggingOut.current = true;

      // Clear state without calling the logout mutation (session is already invalid)
      setUser(null);
      setActiveChannel(null);
      setAuthToken(null);
      setChannelSelectionResolved(true);
      clearPersistedChannelSelection();
      router.push('/login');

      // Reset the guard after a short delay so future logouts can trigger
      setTimeout(() => { isLoggingOut.current = false; }, 2000);
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, [router, setActiveChannel]);

  const hydrateFromMe = useCallback((userData: User): AuthRefreshResult => {
    const normalizedUserData: User = {
      ...userData,
      channels: userData.channels.map(channel => ({
        ...channel,
        permissions: Array.isArray(channel.permissions) ? channel.permissions : [],
      })),
    };

    setUser(normalizedUserData);

    const tenantChannels = normalizedUserData.channels.filter(c => c.code !== '__default_channel__');

    // Try to restore previously selected channel.
    // Channel ids can change across sessions/environments, so prefer id first
    // but fall back to code and then token before forcing store selection.
    const savedChannelId = localStorage.getItem(ACTIVE_CHANNEL_KEY);
    const savedChannelCode = localStorage.getItem(ACTIVE_CHANNEL_CODE_KEY);
    const savedChannelToken = localStorage.getItem('kayn_channel_token');

    // Coerce IDs to strings — backend may return numeric IDs while localStorage stores strings
    const channelMatches = (c: Channel) =>
      String(c.id) === String(savedChannelId) ||
      c.code === savedChannelCode ||
      (c.token && c.token === savedChannelToken);

    const savedChannel = normalizedUserData.channels.find(channelMatches);

    // Also validate the activeChannel that was restored from localStorage on initial render.
    // Read from ref (not closure) to always get the latest value without needing it in deps.
    const currentChannel = activeChannelRef.current;
    const currentStillValid = currentChannel
      ? normalizedUserData.channels.some(c =>
          String(c.id) === String(currentChannel.id) ||
          c.code === currentChannel.code ||
          (c.token && c.token === currentChannel.token)
        )
      : false;

    if (savedChannel) {
      // If the saved channel is the default channel but tenant channels exist,
      // skip it — the user likely wants their merchant channel, not god-mode.
      if (savedChannel.code === '__default_channel__' && tenantChannels.length > 0) {
        // Fall through to auto-select a tenant channel below
      } else {
        setActiveChannel(savedChannel);
        setChannelSelectionResolved(true);
        return { success: true, needsChannelSelection: false };
      }
    }

    // If the current activeChannel (from full localStorage object) is still valid,
    // keep it instead of discarding. This prevents unnecessary redirects when
    // the separate key lookups fail due to type mismatches or missing fields.
    if (currentStillValid && currentChannel) {
      setActiveChannel(currentChannel);
      setChannelSelectionResolved(true);
      return { success: true, needsChannelSelection: false };
    }

    // Any persisted selection that doesn't match the current Me payload is stale.
    // Clear it before falling back so the layout can safely decide whether a
    // real store-selection redirect is needed.
    if (savedChannelId || savedChannelCode || savedChannelToken) {
      clearPersistedChannelSelection();
    }

    if (tenantChannels.length === 1) {
      // Auto-select the single tenant channel (skip default channel)
      setActiveChannel(tenantChannels[0]);
      setChannelSelectionResolved(true);
      return { success: true, needsChannelSelection: false };
    }

    if (tenantChannels.length === 0 && normalizedUserData.channels.length === 1) {
      // Only the default channel exists (platform admin with no tenants)
      setActiveChannel(normalizedUserData.channels[0]);
      setChannelSelectionResolved(true);
      return { success: true, needsChannelSelection: false };
    }

    // Multiple channels and no selection yet
    setActiveChannel(null);
    setChannelSelectionResolved(true);
    return { success: true, needsChannelSelection: normalizedUserData.channels.length > 1 };
  }, [setActiveChannel]);

  const refreshAuth = useCallback(async (allowCookieRefresh = true): Promise<AuthRefreshResult> => {
    try {
      const client = getClient();
      const result = await client.query(ME_QUERY, {}).toPromise();

      // Distinguish transient network errors from real auth failures.
      // urql populates `networkError` on fetch failures (backend down, CORS block,
      // timeout). In that case we must NOT wipe the stored JWT / channel token —
      // otherwise any backend hiccup forces an unnecessary re-login. We just
      // return failure and let the caller decide (UI stays on current page).
      //
      // However, when the middleware blocks with a 401 before GraphQL execution,
      // urql may categorize that as a networkError. Check the message first.
      if (result.error?.networkError) {
        const msg = (result.error.message || '').toLowerCase();
        const status = (result.error as unknown as { response?: { status?: number } })?.response?.status;
        const isAuthFailure = status === 401 || status === 403 || [
          'not authenticated',
          'session has expired',
          'must be logged in',
          'token has expired',
          'invalid or expired',
          'invalid token',
          'unauthorized',
        ].some(p => msg.includes(p)) || msg.includes('401') || msg.includes('403');

        if (isAuthFailure) {
          if (allowCookieRefresh && await refreshAccessTokenFromCookie()) {
            return refreshAuth(false);
          }
          setUser(null);
          setActiveChannel(null);
          setAuthToken(null);
          setChannelSelectionResolved(true);
          clearPersistedChannelSelection();
          return { success: false, error: 'Not authenticated' };
        }

        console.warn('Auth refresh: network error, preserving session', result.error.networkError.message);
        return { success: false, error: 'network' };
      }

      if (result.data?.me) {
        const userData = result.data.me as User;
        return hydrateFromMe(userData);
      }

      // Real "not authenticated" path: backend reachable but returned no user.
      // The stored token is invalid/expired — wipe everything and force login.
      if (allowCookieRefresh && await refreshAccessTokenFromCookie()) {
        return refreshAuth(false);
      }
      setUser(null);
      setActiveChannel(null);
      setAuthToken(null);
      setChannelSelectionResolved(true);
      clearPersistedChannelSelection();
      return { success: false, error: 'Not authenticated' };
    } catch (error) {
      // urql rarely throws (network errors come back as result.error.networkError),
      // but if fetchWithTimeout aborts or something else fails, treat as network.
      console.warn('Auth refresh threw, preserving session:', error);
      return { success: false, error: 'network' };
    }
  }, [hydrateFromMe, setActiveChannel, refreshAccessTokenFromCookie]);

  const checkAuth = async () => {
    try {
      if (isPublicAuthPath()) {
        setChannelSelectionResolved(true);
        return;
      }

      // Fast path: if there's no stored access token, try the protected refresh
      // cookie before treating the browser as logged out.
      const hasStoredToken = typeof window !== 'undefined'
        ? !!sessionStorage.getItem('kayn_auth_token')
        : false;
      if (!hasStoredToken) {
        const refreshedFromCookie = await refreshAccessTokenFromCookie();
        if (!refreshedFromCookie) {
          setChannelSelectionResolved(true);
          return;
        }
      }

      // Initial attempt. If the backend is reachable we're done in one round-trip.
      let result = await refreshAuth();

      // Transparent retry: if the backend is temporarily unreachable (dev restart,
      // network blip, CORS) the JWT is still valid — retry a few times before
      // giving up. This keeps the spinner visible and the page auto-recovers the
      // moment the backend comes back, without forcing a re-login.
      const retryDelays = [1000, 2000, 3000];
      for (let i = 0; i < retryDelays.length && result.error === 'network'; i++) {
        await new Promise(resolve => setTimeout(resolve, retryDelays[i]));
        result = await refreshAuth();
      }
    } finally {
      setLoading(false);
    }
  };

  // A brief API restart or a refresh-cookie race must not strand the dashboard
  // on a false session-error screen. Keep the preserved bearer session alive
  // and recover quietly when the backend becomes reachable again.
  useEffect(() => {
    if (loading || user || isPublicAuthPath()) return;
    if (!sessionStorage.getItem('kayn_auth_token')) return;

    let retryInFlight = false;
    const retry = async () => {
      if (retryInFlight) return;
      retryInFlight = true;
      try {
        await refreshAuth();
      } finally {
        retryInFlight = false;
      }
    };

    const intervalId = window.setInterval(retry, 5000);
    return () => window.clearInterval(intervalId);
  }, [loading, user, refreshAuth]);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const client = getPublicAuthClient();
      const result = await client.mutation(ADMIN_LOGIN, { username, password }).toPromise();
      const loginData = result.data?.adminLogin as
        | {
            success: boolean;
            twoFactorRequired?: boolean | null;
            twoFactorToken?: string | null;
            errorCode?: string | null;
          }
        | undefined;

      if (!loginData?.success) {
        const graphQlMessages = result.error?.graphQLErrors.map((error) => error.message) ?? [];
        return {
          success: false,
          error: resolveLoginErrorCode(loginData?.errorCode, graphQlMessages),
        };
      }

      if (loginData.twoFactorRequired) {
        return {
          success: true,
          twoFactorRequired: true,
          twoFactorToken: loginData.twoFactorToken || undefined,
        };
      }

      // Always use the JWT from the login response for the Authorization header.
      // The TenantResolverMiddleware reads the channelId claim from the JWT to
      // resolve tenant context, then bridges it to a commerce kernel session internally.
      // Using a raw commerce kernel session token instead would break tenant resolution
      // because session tokens don't carry channelId claims.
      const fullLoginData = result.data?.adminLogin as any;
      if (fullLoginData?.token) {
        setAuthToken(fullLoginData.token);
      }

      const refreshed = await refreshAuth();
      if (!refreshed.success) {
        return { success: false, error: refreshed.error || 'Login failed' };
      }

      return {
        success: true,
        needsChannelSelection: refreshed.needsChannelSelection,
      };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'An error occurred' };
    }
  };

  const logout = async () => {
    try {
      const client = getLogoutClient();
      await client.mutation(LOGOUT_MUTATION, {}).toPromise();
      setUser(null);
      setActiveChannel(null);
      setAuthToken(null);
      setChannelSelectionResolved(true);
      clearPersistedChannelSelection();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Select channel from the store selection page
  const selectChannel = useCallback((channelId: string) => {
    if (!user) return;
    const channel = findChannelById(user.channels, channelId);
    if (channel) {
      setActiveChannel(channel);
    }
  }, [user, setActiveChannel]);

  // Switch channel (alias for selectChannel, kept for compatibility)
  const switchChannel = selectChannel;

  // Clear channel selection (used when switching stores)
  // Note: This clears the token but keeps localStorage so we remember the selection for next login
  const clearChannelSelection = useCallback(() => {
    setActiveChannel(null);
  }, [setActiveChannel]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      activeChannel,
      channelSelectionResolved,
      needsChannelSelection,
      login,
      refreshAuth,
      logout,
      selectChannel,
      switchChannel,
      clearChannelSelection,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
